import styled from '@emotion/styled';
import { useState } from 'react';

/* ─── SHARED LAYOUT ─────────────────────────────────────────────────────── */
const Section = styled.section<{ $alt?: boolean }>`
  background: ${p => (p.$alt ? 'var(--color-bg-surface)' : 'var(--color-bg-base)')};
  border-top: 1px solid var(--color-border);
  padding: var(--space-xl) var(--space-lg);
`;

const Container = styled.div`
  margin: 0 auto;
  max-width: var(--max-width-app);
  width: 100%;
`;

const SectionHead = styled.div`
  margin-bottom: var(--space-xl);
`;

const SectionEyebrow = styled.p`
  color: var(--color-accent);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-label);
  margin: 0 0 var(--space-sm) 0;
  text-transform: uppercase;
`;

const SectionH2 = styled.h2`
  color: var(--color-text-primary);
  font-family: var(--font-ui);
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: var(--font-weight-bold);
  line-height: 1.3;
  margin: 0 0 var(--space-sm) 0;
`;

const SectionLead = styled.p`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: var(--font-size-body);
  line-height: 1.75;
  margin: 0;
  max-width: 680px;
`;

const Grid2 = styled.div`
  display: grid;
  gap: var(--space-xl);
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const Grid3 = styled.div`
  display: grid;
  gap: var(--space-md);
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Grid4 = styled.div`
  display: grid;
  gap: var(--space-md);
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const Card = styled.div`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
`;

const CardEyebrow = styled.p`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
  margin: 0 0 var(--space-sm) 0;
  text-transform: uppercase;
`;

const CardTitle = styled.h3`
  color: var(--color-text-primary);
  font-family: var(--font-ui);
  font-size: 1.05rem;
  font-weight: var(--font-weight-bold);
  line-height: 1.3;
  margin: 0 0 var(--space-sm) 0;
`;

const CardBody = styled.p`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: 0.875rem;
  line-height: 1.7;
  margin: 0;
`;

const Accent = styled.strong<{ $color?: string }>`
  color: ${p => p.$color ?? 'var(--color-accent)'};
  font-weight: var(--font-weight-medium);
`;

/* ─── ABOUT SECTION ─────────────────────────────────────────────────────── */
const AboutGrid = styled.div`
  display: grid;
  gap: var(--space-xl);
  grid-template-columns: 1fr 1fr;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AboutText = styled.p`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: var(--font-size-body);
  line-height: 1.8;
  margin: 0 0 var(--space-md) 0;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const StatPill = styled.div`
  align-items: baseline;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  display: flex;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
`;

const PillNum = styled.span<{ $color: string }>`
  color: ${p => p.$color};
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: var(--font-weight-bold);
  line-height: 1;
`;

const PillLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  line-height: 1.3;
  text-transform: uppercase;
`;

const AppStatGrid = styled.div`
  display: grid;
  gap: var(--space-sm);
  grid-template-columns: 1fr 1fr;
  align-content: start;
`;

/* ─── METRIC CARDS ──────────────────────────────────────────────────────── */
const MetricCard = styled(Card)<{ $accent: string }>`
  border-top: 2px solid ${p => p.$accent};
`;

const MetricNumber = styled.div<{ $color: string }>`
  color: ${p => p.$color};
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: var(--font-weight-bold);
  line-height: 1;
  margin-bottom: var(--space-sm);
`;

const FormulaBox = styled.code`
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-accent);
  display: block;
  font-family: var(--font-display);
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  margin-top: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
`;

/* ─── DIFFICULTY SECTION ────────────────────────────────────────────────── */
const DiffCard = styled(Card)<{ $color: string }>`
  border-left: 3px solid ${p => p.$color};
`;

const DiffBadge = styled.span<{ $color: string; $bg: string }>`
  background: ${p => p.$bg};
  border: 1px solid ${p => p.$color};
  border-radius: 100px;
  color: ${p => p.$color};
  display: inline-block;
  font-family: var(--font-ui);
  font-size: 0.65rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-label);
  margin-bottom: var(--space-md);
  padding: 3px 12px;
  text-transform: uppercase;
`;

const DiffList = styled.ul`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  line-height: 1.7;
  list-style: none;
  margin: var(--space-sm) 0 0 0;
  padding: 0;

  li::before {
    color: var(--color-text-muted);
    content: '– ';
  }
`;

/* ─── TIPS SECTION ──────────────────────────────────────────────────────── */
const TipsList = styled.ol`
  counter-reset: tips;
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  list-style: none;
  margin: 0;
  padding: 0;
`;

const TipItem = styled.li`
  align-items: flex-start;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  counter-increment: tips;
  display: flex;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
`;

const TipNumber = styled.span`
  color: var(--color-accent);
  flex-shrink: 0;
  font-family: var(--font-display);
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.1em;
  min-width: 28px;
  padding-top: 2px;
`;

const TipContent = styled.div``;

const TipTitle = styled.h3`
  color: var(--color-text-primary);
  font-family: var(--font-ui);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-medium);
  margin: 0 0 4px 0;
`;

const TipBody = styled.p`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: 0.875rem;
  line-height: 1.65;
  margin: 0;
`;

/* ─── FAQ ───────────────────────────────────────────────────────────────── */
const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const FaqItem = styled.article``;

const FaqQuestion = styled.button<{ $open: boolean }>`
  align-items: center;
  background: var(--color-bg-elevated);
  border: 1px solid ${p => (p.$open ? 'var(--color-border-strong)' : 'var(--color-border)')};
  border-bottom-left-radius: ${p => (p.$open ? '0' : 'var(--radius-md)')};
  border-bottom-right-radius: ${p => (p.$open ? '0' : 'var(--radius-md)')};
  border-top-left-radius: var(--radius-md);
  border-top-right-radius: var(--radius-md);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  font-family: var(--font-ui);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  justify-content: space-between;
  padding: var(--space-md) var(--space-lg);
  text-align: left;
  transition: all var(--transition-base);
  width: 100%;

  &:hover {
    background: var(--color-bg-subtle);
  }
`;

const FaqChevron = styled.span<{ $open: boolean }>`
  color: var(--color-accent);
  flex-shrink: 0;
  font-size: 0.75rem;
  margin-left: var(--space-md);
  transform: ${p => (p.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform var(--transition-base);
`;

const FaqAnswer = styled.div<{ $open: boolean }>`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-strong);
  border-top: none;
  border-bottom-left-radius: var(--radius-md);
  border-bottom-right-radius: var(--radius-md);
  color: var(--color-text-secondary);
  display: ${p => (p.$open ? 'block' : 'none')};
  font-family: var(--font-ui);
  font-size: 0.9375rem;
  line-height: 1.75;
  padding: var(--space-md) var(--space-lg);
`;

const Separator = styled.hr`
  border: none;
  border-top: 1px solid var(--color-border);
  margin: 0;
`;

/* ─── FAQ DATA ──────────────────────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    q: 'What is a good typing speed (WPM)?',
    a: 'The average typing speed is around 40 WPM (words per minute). A speed of 60–80 WPM is considered proficient and suitable for most professional roles. Speeds above 80 WPM are fast, and anything above 100 WPM places you in the top 10% of typists globally. Professional typists and stenographers typically reach 120–150 WPM.',
  },
  {
    q: 'What is the difference between WPM and raw WPM?',
    a: 'WPM (net WPM) counts only correctly typed characters — it penalizes errors. Raw WPM counts every keystroke regardless of whether the character was correct or later corrected. The gap between your WPM and raw WPM reveals how much errors are costing you. A large gap means you are making many mistakes; a small gap means your accuracy is high.',
  },
  {
    q: 'How is WPM calculated in a typing test?',
    a: 'WPM is calculated using the formula: (correct characters ÷ 5) ÷ minutes elapsed. The number 5 represents the average word length in English. So if you correctly type 300 characters in 1 minute, your WPM is 300 ÷ 5 = 60 WPM. Cryotype uses this standard formula for both WPM and raw WPM.',
  },
  {
    q: 'How can I improve my typing speed?',
    a: 'The most effective method is to prioritize accuracy over speed — errors slow you down more than hesitation does. Learn proper touch typing with all 10 fingers on the home row (ASDF and JKL;). Practice daily in short 15–30 minute sessions. Avoid looking at the keyboard. Use a consistent, relaxed posture. With dedicated practice, most people can increase their speed by 15–25 WPM within a few weeks.',
  },
  {
    q: 'How long does it take to reach 100 WPM?',
    a: 'For someone starting from scratch, reaching 100 WPM typically takes 6–12 months of consistent daily practice (15–30 minutes per day). If you are already at 60–70 WPM, reaching 100 WPM usually takes 2–4 months of focused training. The jump from 100 to 120+ WPM is the most difficult and can take a year or more of deliberate practice.',
  },
  {
    q: 'Is touch typing faster than hunt-and-peck?',
    a: 'Yes, significantly. The average hunt-and-peck typist reaches 35–45 WPM due to the time lost scanning the keyboard. Trained touch typists average 50–80 WPM and can reach 120+ WPM because their fingers move to the correct keys from muscle memory without any visual scanning. The initial investment of learning touch typing pays off quickly.',
  },
  {
    q: 'What difficulty level should I choose?',
    a: 'Choose Easy if you are a beginner or want to build speed with short, common words. Choose Medium (the default) for balanced practice that reflects everyday typing. Choose Hard if you want to challenge yourself with longer, less-common words, numbers, and symbols — this mode is closest to real-world typing tasks like coding or writing.',
  },
  {
    q: 'Does keyboard type affect typing speed?',
    a: 'Yes, to a degree. Mechanical keyboards with tactile or clicky switches help many typists because the physical feedback confirms each keypress, reducing errors. However, layout matters more than hardware — QWERTY is the most widely used and tested layout. Switching to Dvorak or Colemak can marginally improve efficiency for some people, but the learning curve is significant.',
  },
];

/* ─── FAQ ITEM COMPONENT ────────────────────────────────────────────────── */
function FaqEntry({ q, a, defaultOpen = false }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <FaqItem itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <FaqQuestion $open={open} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span itemProp="name">{q}</span>
        <FaqChevron $open={open}>&#9660;</FaqChevron>
      </FaqQuestion>
      <FaqAnswer $open={open} itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
        <span itemProp="text">{a}</span>
      </FaqAnswer>
    </FaqItem>
  );
}

/* ─── MAIN EXPORT ───────────────────────────────────────────────────────── */
export default function InfoSections() {
  return (
    <>
      {/* ── ABOUT ─────────────────────────────────────────────────────── */}
      <Section aria-labelledby="about-heading">
        <Container>
          <AboutGrid>
            <div>
              <SectionH2 id="about-heading">
                Free Online Typing Test — Measure Your WPM &amp; Accuracy
              </SectionH2>
              <AboutText>
                Cryotype is a{' '}
                <Accent>free, browser-based typing speed test</Accent> designed for
                precision. Every test tracks your words per minute, raw WPM, accuracy, and
                character-level error data in real time — giving you a clear picture of where
                you are and what to work on.
              </AboutText>
              <AboutText>
                Choose from three difficulty levels and four time modes. Results are saved
                locally so you can track your improvement session over session. Share your
                score as text or a downloadable image.
              </AboutText>
            </div>

            <AppStatGrid aria-label="App statistics">
              <StatPill>
                <PillNum $color="var(--color-wpm)">4</PillNum>
                <PillLabel>Time modes<br />(15 · 30 · 60 · 120s)</PillLabel>
              </StatPill>
              <StatPill>
                <PillNum $color="var(--color-correct)">3</PillNum>
                <PillLabel>Difficulty<br />levels</PillLabel>
              </StatPill>
              <StatPill>
                <PillNum $color="var(--color-accent)">500+</PillNum>
                <PillLabel>Words across<br />all modes</PillLabel>
              </StatPill>
              <StatPill>
                <PillNum $color="var(--color-time)">20</PillNum>
                <PillLabel>Tests saved<br />in history</PillLabel>
              </StatPill>
            </AppStatGrid>
          </AboutGrid>
        </Container>
      </Section>

      <Separator />

      {/* ── METRICS EXPLAINED ─────────────────────────────────────────── */}
      <Section $alt aria-labelledby="metrics-heading">
        <Container>
          <SectionHead>
            <SectionEyebrow>Understanding your results</SectionEyebrow>
            <SectionH2 id="metrics-heading">What Every Metric Means</SectionH2>
            <SectionLead>
              Cryotype tracks four distinct metrics after each test so you know exactly what
              to focus on next.
            </SectionLead>
          </SectionHead>

          <Grid4>
            <MetricCard $accent="var(--color-wpm)">
              <CardEyebrow>Primary metric</CardEyebrow>
              <MetricNumber $color="var(--color-wpm)">WPM</MetricNumber>
              <CardTitle>Words Per Minute</CardTitle>
              <CardBody>
                Net WPM counts only correctly typed characters divided by 5 (the standard
                English word length), over the elapsed time. Errors are fully penalised.
              </CardBody>
              <FormulaBox>(correct chars ÷ 5) ÷ minutes</FormulaBox>
            </MetricCard>

            <MetricCard $accent="var(--color-text-muted)">
              <CardEyebrow>Secondary metric</CardEyebrow>
              <MetricNumber $color="var(--color-text-secondary)">Raw</MetricNumber>
              <CardTitle>Raw WPM</CardTitle>
              <CardBody>
                All keystrokes counted regardless of errors. The gap between WPM and raw WPM
                directly shows how much mistakes are costing you in speed.
              </CardBody>
              <FormulaBox>(all chars ÷ 5) ÷ minutes</FormulaBox>
            </MetricCard>

            <MetricCard $accent="var(--color-accuracy)">
              <CardEyebrow>Quality metric</CardEyebrow>
              <MetricNumber $color="var(--color-accuracy)">ACC</MetricNumber>
              <CardTitle>Accuracy</CardTitle>
              <CardBody>
                The percentage of correct characters out of everything you typed. Below 95%
                accuracy usually means speed is outrunning control — slow down to speed up.
              </CardBody>
              <FormulaBox>correct ÷ (correct + wrong + extra)</FormulaBox>
            </MetricCard>

            <MetricCard $accent="var(--color-time)">
              <CardEyebrow>Trend metric</CardEyebrow>
              <MetricNumber $color="var(--color-time)">Chart</MetricNumber>
              <CardTitle>WPM Over Time</CardTitle>
              <CardBody>
                The post-test line chart shows your WPM at every second. Drops reveal fatigue
                or focus loss. A flat line means great consistency.
              </CardBody>
              <FormulaBox>sampled every 1 second</FormulaBox>
            </MetricCard>
          </Grid4>
        </Container>
      </Section>

      <Separator />

      {/* ── DIFFICULTY ────────────────────────────────────────────────── */}
      <Section aria-labelledby="difficulty-heading">
        <Container>
          <SectionHead>
            <SectionEyebrow>Choose your challenge</SectionEyebrow>
            <SectionH2 id="difficulty-heading">Difficulty Levels Explained</SectionH2>
            <SectionLead>
              Each difficulty uses a different word bank and content mix. Pick the one that
              challenges you just enough to grow.
            </SectionLead>
          </SectionHead>

          <Grid3>
            <DiffCard $color="var(--color-correct)">
              <DiffBadge $color="var(--color-correct)" $bg="rgba(52,211,153,0.1)">
                Easy
              </DiffBadge>
              <CardTitle>Short Common Words</CardTitle>
              <CardBody>
                A curated bank of 150+ everyday words, mostly 2–4 characters long. Ideal for
                beginners building speed, or anyone warming up before a harder session.
              </CardBody>
              <DiffList>
                <li>2–4 character words</li>
                <li>High-frequency vocabulary</li>
                <li>No numbers or symbols</li>
                <li>Best for: 20–60 WPM typists</li>
              </DiffList>
            </DiffCard>

            <DiffCard $color="var(--color-wpm)">
              <DiffBadge $color="var(--color-wpm)" $bg="rgba(129,140,248,0.1)">
                Medium
              </DiffBadge>
              <CardTitle>Mixed Vocabulary</CardTitle>
              <CardBody>
                The default mode. Over 300 words ranging from 3 to 8 characters — a
                representative mix of everyday English that reflects typical writing tasks.
              </CardBody>
              <DiffList>
                <li>3–8 character words</li>
                <li>Common English vocabulary</li>
                <li>No numbers or symbols</li>
                <li>Best for: 40–100 WPM typists</li>
              </DiffList>
            </DiffCard>

            <DiffCard $color="var(--color-incorrect)">
              <DiffBadge $color="var(--color-incorrect)" $bg="rgba(248,113,113,0.1)">
                Hard
              </DiffBadge>
              <CardTitle>Advanced + Numbers &amp; Symbols</CardTitle>
              <CardBody>
                Long, technical, and complex vocabulary combined with injected numbers and
                punctuation marks. Closest to real-world typing in professional contexts.
              </CardBody>
              <DiffList>
                <li>7–15 character complex words</li>
                <li>Numbers injected (~8% frequency)</li>
                <li>Symbols: , . ? ! ;</li>
                <li>Best for: 70+ WPM typists</li>
              </DiffList>
            </DiffCard>
          </Grid3>
        </Container>
      </Section>

      <Separator />

      {/* ── TIPS ──────────────────────────────────────────────────────── */}
      <Section $alt aria-labelledby="tips-heading">
        <Container>
          <Grid2>
            <SectionHead style={{ marginBottom: 0 }}>
              <SectionEyebrow>Deliberate practice</SectionEyebrow>
              <SectionH2 id="tips-heading">How to Improve Your Typing Speed</SectionH2>
              <SectionLead>
                These are the habits that consistently separate slow typists from fast ones.
                Most people see measurable improvement within two to four weeks of applying them.
              </SectionLead>
            </SectionHead>

            <TipsList>
              <TipItem>
                <TipNumber>01</TipNumber>
                <TipContent>
                  <TipTitle>Prioritise accuracy over raw speed</TipTitle>
                  <TipBody>
                    Every correction keystroke costs more time than a brief pause before typing
                    correctly. Target 95%+ accuracy first — speed grows naturally once the
                    muscle memory is clean.
                  </TipBody>
                </TipContent>
              </TipItem>

              <TipItem>
                <TipNumber>02</TipNumber>
                <TipContent>
                  <TipTitle>Master home-row touch typing</TipTitle>
                  <TipBody>
                    Keep your left fingers on A-S-D-F and right fingers on J-K-L-;. Every key
                    on the keyboard is reachable from this position without looking. This single
                    change can double your speed ceiling.
                  </TipBody>
                </TipContent>
              </TipItem>

              <TipItem>
                <TipNumber>03</TipNumber>
                <TipContent>
                  <TipTitle>Never look at the keyboard</TipTitle>
                  <TipBody>
                    Cover your hands or use a blank keyboard. Looking down breaks the feedback
                    loop between brain and fingers. It feels slow at first — push through the
                    first two weeks and it becomes automatic.
                  </TipBody>
                </TipContent>
              </TipItem>

              <TipItem>
                <TipNumber>04</TipNumber>
                <TipContent>
                  <TipTitle>Practice in short, consistent sessions</TipTitle>
                  <TipBody>
                    15 minutes of focused daily practice beats 2 hours once a week. Use the 15s
                    and 30s modes for high-intensity sprints. Let muscle memory consolidate
                    overnight between sessions.
                  </TipBody>
                </TipContent>
              </TipItem>

              <TipItem>
                <TipNumber>05</TipNumber>
                <TipContent>
                  <TipTitle>Track your progress and analyse errors</TipTitle>
                  <TipBody>
                    Use the History panel to spot trends. If your WPM-over-time chart drops
                    sharply near the end, stamina is the issue. If accuracy is consistently
                    below 90%, specific key combinations are causing errors — drill those.
                  </TipBody>
                </TipContent>
              </TipItem>
            </TipsList>
          </Grid2>
        </Container>
      </Section>

      <Separator />

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <Section
        aria-labelledby="faq-heading"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <Container>
          <SectionHead>
            <SectionEyebrow>Frequently asked questions</SectionEyebrow>
            <SectionH2 id="faq-heading">Typing Test FAQ</SectionH2>
            <SectionLead>
              Common questions about WPM, accuracy, and how to get the most out of Cryotype.
            </SectionLead>
          </SectionHead>

          <FaqList>
            {FAQ_ITEMS.map((item, i) => (
              <FaqEntry key={i} q={item.q} a={item.a} defaultOpen={i === 0} />
            ))}
          </FaqList>
        </Container>
      </Section>
    </>
  );
}
